import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  CardGiftcard,
  QrCodeScanner,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from "./../images/logo.png";


const CustomerHeader = ({ onStartQRScanner, onShowLogin }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuAnchor(null);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuAnchor(null);
  };

  const handleScrollToFeatures = () => {
    setMobileMenuAnchor(null);
    const currentPath = window.location.pathname;
    
    if (currentPath === '/' || currentPath === '') {
      // Already on landing page, scroll to section
      setTimeout(() => {
        const featuresSection = document.getElementById('features-section');
        if (featuresSection) {
          featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Navigate to landing page first, then scroll
      navigate('/');
      setTimeout(() => {
        const featuresSection = document.getElementById('features-section');
        if (featuresSection) {
          featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  };
  const handleScrollToContactUs = () => {
    setMobileMenuAnchor(null);
    const currentPath = window.location.pathname;
    
    if (currentPath === '/' || currentPath === '') {
      // Already on landing page, scroll to section
      setTimeout(() => {
        const contactUsSection = document.getElementById('contact-us-section');
        if (contactUsSection) {
          contactUsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Navigate to landing page first, then scroll
      navigate('/');
      setTimeout(() => {
        const contactUsSection = document.getElementById('contact-us-section');
        if (contactUsSection) {
          contactUsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  };

  return (
    <>
      <AppBar position="static" color="inherit" elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 8 } }}>
          {/* Mobile Menu Button */}
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
            sx={{ 
              mr: 2,
              display: { xs: 'block', md: 'none' },
              color: '#333'
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
            {/* Geometric Icon */}
            <img src={logo} alt="logo" style={{ width: '250px' }} />

          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
            <Button
              sx={{
                fontWeight: 500,
                color: "#333",
                "&:hover": { color: "#E53935" },
              }}
              onClick={() => navigate('/')}
            >
              HOME
            </Button>
            <Button
              onClick={handleScrollToFeatures}
              sx={{
                fontWeight: 500,
                color: "#333",
                "&:hover": { color: "#E53935" },
              }}
            >
              FEATURES
            </Button>
            <Button
              sx={{
                fontWeight: 500,
                color: "#333",
                "&:hover": { color: "#E53935" },
              }}
              onClick={() => navigate('/verify')}
            >
              AUTHENTICITY
            </Button>
            
            {/* Additional User-specific Navigation */}
            {user && (
              <>
                <Button
                  sx={{
                    fontWeight: 500,
                    color: "#333",
                    "&:hover": { color: "#E53935" },
                  }}
                  onClick={() => navigate('/redeem')}
                >
                  REDEEM CARDS
                </Button>
                <Button
                  sx={{
                    fontWeight: 500,
                    color: "#333",
                    "&:hover": { color: "#E53935" },
                  }}
                  onClick={handleLogout}
                >
                  LOGOUT
                </Button>
              </>
            )}
            
            {!user && (
              <>
                <Button
                  sx={{
                    fontWeight: 500,
                    color: "#333",
                    "&:hover": { color: "#E53935" },
                  }}
                  onClick={() => navigate('/auth')}
                >
                  LOYALTY
                </Button>
              </>
            )}
            <Button
              sx={{
                fontWeight: 500,
                color: "#333",
                "&:hover": { color: "#E53935" },
              }}
              onClick={handleScrollToContactUs}
            >
              CONTACT US
          </Button>
          </Box>
        </Toolbar>
        
        {/* Bottom Border Line */}
        <Box
          sx={{
            height: '1px',
            backgroundColor: '#e0e0e0',
            width: '100%'
          }}
        />
      </AppBar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: 3,
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {/* Main Navigation - Always Visible */}
        <MenuItem 
          onClick={() => handleNavigation('/')}
          sx={{ 
            '&:hover': { backgroundColor: '#f5f5f5' },
            color: '#333'
          }}
        >
          HOME
        </MenuItem>
        <MenuItem 
          onClick={handleScrollToFeatures}
          sx={{ 
            '&:hover': { backgroundColor: '#f5f5f5' },
            color: '#333'
          }}
        >
          FEATURES
        </MenuItem>
        <MenuItem 
          onClick={() => handleNavigation('/verify')}
          sx={{ 
            '&:hover': { backgroundColor: '#f5f5f5' },
            color: '#333'
          }}
        >
          AUTHENTICITY
        </MenuItem>
        
        {/* User-specific Navigation */}
        {user ? (
          <>
            <MenuItem 
              onClick={() => handleNavigation('/redeem')}
              sx={{ 
                '&:hover': { backgroundColor: '#f5f5f5' },
                color: '#333'
              }}
            >
              <CardGiftcard sx={{ mr: 2, color: '#E53935' }} />
              REDEEM CARDS
            </MenuItem>
            <MenuItem 
              onClick={handleLogout}
              sx={{ 
                '&:hover': { backgroundColor: '#f5f5f5' },
                color: '#E53935'
              }}
            >
              LOGOUT
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem 
              onClick={() => handleNavigation('/auth')}
              sx={{ 
                '&:hover': { backgroundColor: '#f5f5f5' },
                color: '#333'
              }}
            >
              LOYALTY
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default CustomerHeader;
